import styles from './index.module.scss';
import { defineComponent, h } from 'vue';
import { Dialog } from '@phi/components';
import { toAngles } from '../../../vector3';
import {
    get_display_unit_from_unitless_value,
    get_unitless_value_from_display_unit,
} from '../../../utility';
import { Sections, Section } from '@phi/components/layout';
import { FormNumber, FormWithLabels, Label } from '@components/form';
import { CenterEditor } from './components/center-editor';
import { TiltEditor } from './components/tilt-editor';
import { finish_making_circular_selected_edges_or_face } from '@frontend/circularity';
import { round_decimals } from '@features/decimal-approximation';

export default defineComponent({
    props: {
        show: { required: true, type: Boolean},
    },

    emits: {
        'update:show': (x: boolean) => true,
    },

    data() {
        return {
            diameter: 0,
            phi: 0,
            theta: 0,
            centerX: 0,
            centerY: 0,
            centerZ: 0,
        };
    },

    watch: {
        show(value: typeof this.show) {
            if (value) this.reset();
        },
    },

    mounted() {
        if (this.show)
            this.reset();
    },

    methods: {
        reset() {
            const eps = 0.0001;

            this.centerX = round_decimals(get_display_unit_from_unitless_value(
                Math.abs(global_exact_circle_center_coordinates.x) > eps
                    ? global_exact_circle_center_coordinates.x
                    : 0,
            ));
            this.centerY = round_decimals(get_display_unit_from_unitless_value(
                Math.abs(global_exact_circle_center_coordinates.y) > eps
                    ? global_exact_circle_center_coordinates.y
                    : 0,
            ));
            this.centerZ = round_decimals(get_display_unit_from_unitless_value(
                Math.abs(global_exact_circle_center_coordinates.z) > eps
                    ? global_exact_circle_center_coordinates.z
                    : 0,
            ));
            this.diameter = round_decimals(get_display_unit_from_unitless_value(
                Math.abs(global_exact_circle_radius * 2.0) > eps
                    ? global_exact_circle_radius * 2.0
                    : 0
            ));

            const [ theta, phi ] = toAngles(global_exact_circle_normal);
            this.theta = round_decimals(theta * 180 / Math.PI);
            this.phi = round_decimals(phi * 180 / Math.PI);
        },

        onSubmit() {
            finish_making_circular_selected_edges_or_face(
                true,
                get_unitless_value_from_display_unit(this.centerX),
                get_unitless_value_from_display_unit(this.centerY),
                get_unitless_value_from_display_unit(this.centerZ),
                get_unitless_value_from_display_unit(this.diameter),
                this.theta,
                this.phi);
            this.$emit('update:show', false);
        },

        onClose() {
            this.$emit('update:show', false);
        }
    },

    render() {
        return h(Dialog, {
            show: this.show,
            id: 'drawExactCircle',
            title: 'Draw exact circle',
            confirm_label: 'Set',
            onClose: this.onClose,
            onConfirm: this.onSubmit,
            'onUpdate:show': x => this.$emit('update:show', x),
            width: 'narrower',
        }, () => h(Sections, {
            gap: 'wider',
            separator: 'border',
        }, () => [
            h(Section, { horizontal: true }, {
                header: () => h('header', { class: styles.header }, 'Center'),
                body: () => h(CenterEditor, {
                    x: this.centerX,
                    y: this.centerY,
                    z: this.centerZ,
                    'onUpdate:x': x => this.centerX = round_decimals(x),
                    'onUpdate:y': x => this.centerY = round_decimals(x),
                    'onUpdate:z': x => this.centerZ = round_decimals(x),
                }),
            }),

            h(Section, { horizontal: true }, {
                header: () => h('header', { class: styles.header }, 'Diameter'),
                body: () => h(FormWithLabels, { width: '2em' }, () => [
                    h(Label, null, {
                        label: () => h('span', 'D'),
                        input: () => h(FormNumber, {
                            step: 0.01,
                            change: true,
                            value: this.diameter,
                            'onUpdate:value': x => this.diameter = round_decimals(x),
                        }),
                    })
                ]),
            }),

            h(Section, { horizontal: true }, {
                header: () => h('header', { class: styles.header }, 'Tilt'),
                body: () => h(TiltEditor, {
                    phi: this.phi,
                    theta: this.theta,
                    'onUpdate:phi': x => this.phi = round_decimals(x),
                    'onUpdate:theta': x => this.theta = round_decimals(x),
                }),
            })
        ]));
    }
});
